import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { CodeXml, Loader2 } from "lucide-react";

import { cn } from "../../lib/utils";

import { auth } from "../../firebase";
import { signInWithEmailAndPassword } from "firebase/auth";
import analyticsService from "../../services/analyticsService";

import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

import functionsService from "../../services/functionsService";

import { isEmailValid } from "../../helpers/emailHelper";

import { useToast } from "../../components/hooks/use-toast";

const Signup = () => {
  const [searchParams] = useSearchParams();

  // Check if invite code is provided in the URL
  const urlToken = searchParams.has("inviteCode")
    ? searchParams.get("inviteCode")
    : "";

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteToken, setInviteToken] = useState(urlToken);
  const [isValid, setIsValid] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false);

  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (
      firstName.length > 0 &&
      lastName.length > 0 &&
      isEmailValid(email) &&
      password.length >= 6 &&
      inviteToken.length > 0
    ) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  }, [firstName, lastName, email, password, inviteToken]);

  const signUp = async () => {
    setIsSigningUp(true);

    setTimeout(() => {
      setIsSigningUp(false);
    }, 5000);

    try {
      const signupInfo = { email, password, firstName, lastName };

      const { success, error } = await functionsService.validateInvite(
        inviteToken,
        signupInfo
      );

      if (!success) {
        console.error("Sign up error:", error);
        
        // Track signup validation error
        await analyticsService.trackError('invite_validation_error', error.message, 'signup');

        toast({
          variant: "destructive",
          title: "Uh oh! There was a error signing you up.",
          description: error.message,
        });

        return;
      }

      await signInWithEmailAndPassword(auth, email, password);
      
      // Track successful signup event
      await analyticsService.trackSignUp('invite');
      
      navigate("/");

      toast({
        title: "Sign up",
        description: "Sign up successful.",
      });
    } catch (error) {
      console.error("Sign up error:", error);
      
      // Track signup error
      await analyticsService.trackError('auth_error', error.message, 'signup');

      toast({
        variant: "destructive",
        title: "Uh oh! There was a error signing you up.",
        description: error.message,
      });
    } finally {
      setIsSigningUp(false);
    }
  };

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-muted p-6 md:p-10">
      <div className="flex w-full max-w-sm flex-col gap-6">
        <a href="/" className="flex items-center gap-2 self-center font-medium">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <CodeXml className="size-4" />
          </div>
          Allayze
        </a>

        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-xl">Welcome</CardTitle>

              <CardDescription>
                Sign up to your Allayze account
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form>
                <div className="grid gap-6">
                  <div className="grid gap-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="firstName">First Name</Label>

                        <Input
                          id="firstName"
                          required
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          disabled={isSigningUp}
                        />
                      </div>

                      <div className="grid gap-2">
                        <Label htmlFor="lastName">Last Name</Label>
                        <Input
                          id="lastName"
                          required
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          disabled={isSigningUp}
                        />
                      </div>
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="email">Email</Label>

                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={isSigningUp}
                        placeholder="abc@example.com"
                        required
                      />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="password">Password</Label>

                      <Input
                        id="password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={isSigningUp}
                        required
                      />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="inviteCode">Invite Code</Label>

                      <Input
                        id="inviteCode"
                        value={inviteToken}
                        onChange={(e) => setInviteToken(e.target.value)}
                        disabled={isSigningUp}
                        required
                      />
                    </div>

                    <Button
                      className="w-full"
                      disabled={!isValid || isSigningUp}
                      onClick={signUp}
                    >
                      {isSigningUp ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          <span>Signing up...</span>
                        </>
                      ) : (
                        <>
                          <span>Sign up</span>
                        </>
                      )}
                    </Button>
                  </div>

                  <div
                    className={cn(
                      "text-center text-sm",
                      isSigningUp
                        ? "cursor-none text-muted-foreground pointer-events-none"
                        : ""
                    )}
                  >
                    Already have an account?{" "}
                    <Link to="/login">
                      <span className="underline underline-offset-4">
                        Log in
                      </span>
                    </Link>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>

          <div className="text-balance text-center text-xs text-muted-foreground [&_a]:underline [&_a]:underline-offset-4 [&_a]:hover:text-primary  ">
            By signing up, you agree to our <span>Terms of Service</span> and{" "}
            <span>Privacy Policy</span>.
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
